import { Hash, LayoutGrid, Clock, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";

interface BlockDetailsProps {
  blockId: string;
  blockHeight: number;
  previousBlockHash?: string;
  txCount: number;
  blockTime: number;
}

export default function BlockDetails({ 
  blockId, 
  blockHeight, 
  previousBlockHash, 
  txCount, 
  blockTime 
}: BlockDetailsProps) {
  const { toast } = useToast();

  // Copy text to clipboard with toast notification
  const copyToClipboard = (text: string, type: 'address' | 'txid' | 'amount') => {
    navigator.clipboard.writeText(text);
    
    const messages = {
      address: {
        title: "Copied to clipboard",
        description: "Address has been copied to your clipboard."
      },
      txid: {
        title: "Copied to clipboard", 
        description: "Transaction ID has been copied to your clipboard."
      },
      amount: {
        title: "Copied to clipboard",
        description: "Amount has been copied to your clipboard."
      }
    };

    toast({
      title: messages[type].title,
      description: messages[type].description,
    });
  };

  return (
    <div className="space-y-4">
      {/* Block Hash */}
      <div className="flex items-center">
        <Hash className="h-5 w-5 mr-3 text-blue-600 dark:text-blue-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white">Block Hash</p>
          <div className="flex items-center">
            <span className="font-mono text-sm text-slate-600 dark:text-gray-400">
              {blockId.slice(0, 8)}...{blockId.slice(-8)}
            </span>
            <button 
              onClick={() => copyToClipboard(blockId, 'txid')} 
              className="ml-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Copy block hash"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="flex items-center">
        <Hash className="h-5 w-5 mr-3 text-blue-600 dark:text-blue-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white">Transactions</p>
          <span className="text-sm text-slate-600 dark:text-gray-400">
            {txCount.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Timestamp */}
      <div className="flex items-center">
        <Clock className="h-5 w-5 mr-3 text-blue-600 dark:text-blue-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white">Timestamp</p>
          <span className="text-sm text-slate-600 dark:text-gray-400">
            {formatDate(blockTime, "MM/DD/YYYY, HH:mm:ss")}
          </span>
        </div>
      </div>
    </div>
  );
} 